import React, { useState, useMemo, ReactElement } from 'react';
import { FixedSizeList as List } from 'react-window';
import './DualListSelector.scss';

export interface DualListItem {
    id: number | string;
    name: string;
}

interface DualListSelectorProps {
    available: DualListItem[];
    selectedIds: Array<number | string>;
    onChange: (selectedIds: Array<number | string>) => void;
    headerActions?: React.ReactNode;
    virtualized?: boolean;
}

const DualListSelector = ({
    available,
    selectedIds,
    onChange,
    headerActions,
    virtualized = false,
}: DualListSelectorProps): ReactElement => {
    const [availableFilter, setAvailableFilter] = useState<string>('');
    const [selectedFilter, setSelectedFilter] = useState<string>('');

    const selectedItems = useMemo(
        () => available.filter((item) => selectedIds.includes(item.id)),
        [available, selectedIds]
    );

    const unselectedItems = useMemo(() => {
        const filterLower = availableFilter.toLocaleLowerCase();
        return available
            .filter((item) => !selectedIds.includes(item.id))
            .filter((item) => item.name.toLocaleLowerCase().indexOf(filterLower) >= 0)
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }, [available, selectedIds, availableFilter]);

    const filteredSelectedItems = useMemo(() => {
        const filterLower = selectedFilter.toLocaleLowerCase();
        return selectedItems
            .filter((item) => item.name.toLocaleLowerCase().indexOf(filterLower) >= 0)
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }, [selectedItems, selectedFilter]);

    const selectItem = (id: number | string) => {
        onChange([...selectedIds, id]);
    };

    const deselectItem = (id: number | string) => {
        onChange(selectedIds.filter((sid) => sid !== id));
    };

    const selectAll = () => {
        const newIds = unselectedItems.map((item) => item.id);
        onChange([...selectedIds, ...newIds]);
    };

    const deselectAll = () => {
        onChange([]);
    };

    const renderAvailableList = () => {
        if (virtualized) {
            return (
                <List
                    className='list-group'
                    height={350}
                    itemCount={unselectedItems.length}
                    itemSize={50}
                    width={'100%'}
                    itemData={unselectedItems}
                    itemKey={(index, data) => data[index].id}>
                    {({ data, index, style }) => {
                        const item = data[index];
                        return (
                            <div
                                style={style}
                                className='list-group-item'
                                id={item.id.toString()}
                                onClick={() => selectItem(item.id)}>
                                <div className='d-flex w-100 justify-content-between'>
                                    <span className='list-item-text align-middle'>{item.name}</span>
                                    <div className='list-item-icon'>
                                        <i className='fa-regular fa-plus'></i>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </List>
            );
        }
        return (
            <div className='list-group'>
                {unselectedItems.map((item) => (
                    <div
                        key={item.id}
                        className='list-group-item'
                        id={item.id.toString()}
                        onClick={() => selectItem(item.id)}>
                        <div className='d-flex w-100 justify-content-between'>
                            <span className='list-item-text align-middle'>{item.name}</span>
                            <div className='list-item-icon'>
                                <i className='fa-regular fa-plus'></i>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderSelectedList = () => {
        if (virtualized) {
            return (
                <List
                    className='list-group'
                    height={350}
                    itemCount={filteredSelectedItems.length}
                    itemSize={50}
                    width={'100%'}
                    itemData={filteredSelectedItems}
                    itemKey={(index, data) => data[index].id}>
                    {({ data, index, style }) => {
                        const item = data[index];
                        return (
                            <div
                                style={style}
                                className='list-group-item'
                                id={item.id.toString()}
                                onClick={() => deselectItem(item.id)}>
                                <div className='d-flex w-100 justify-content-between'>
                                    <span className='list-item-text align-middle'>{item.name}</span>
                                    <div className='list-item-icon'>
                                        <i className='fa-regular fa-minus'></i>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                </List>
            );
        }
        return (
            <div className='list-group'>
                {filteredSelectedItems.map((item) => (
                    <div
                        key={item.id}
                        className='list-group-item'
                        id={item.id.toString()}
                        onClick={() => deselectItem(item.id)}>
                        <div className='d-flex w-100 justify-content-between'>
                            <span className='list-item-text align-middle'>{item.name}</span>
                            <div className='list-item-icon'>
                                <i className='fa-regular fa-minus'></i>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className='row dual-list-selector'>
            {/* Available column */}
            <div className='col-md-6'>
                <div className='row projects-section-top'>
                    <div className='col-12 my-3'>
                        <span className='list-title'>Available ({unselectedItems.length})</span>
                    </div>
                </div>

                <div className='row projects-section'>
                    <div className='col-12 py-2'>
                        <div className='input-group'>
                            <input
                                type='text'
                                className='form-control'
                                id='search-available'
                                placeholder='Search by name'
                                aria-label='Search by name'
                                aria-describedby='search-addon'
                                value={availableFilter}
                                onChange={(e) => setAvailableFilter(e.target.value)}
                            />
                            <span className='input-group-text bg-transparent' id='search-addon'>
                                <i className='far fa-magnifying-glass'></i>
                            </span>
                        </div>
                    </div>
                </div>

                <div className='row projects-section'>
                    <div className='col-12 list-container'>{renderAvailableList()}</div>
                </div>

                <div className='row projects-section-bottom'>
                    <div className='col-12'>
                        <button type='button' className='btn pacer61-purple-btn' onClick={() => selectAll()}>
                            Select All
                        </button>
                    </div>
                </div>
            </div>

            {/* Selected column */}
            <div className='col-md-6'>
                <div className='row projects-section-top'>
                    <div className='col-9 my-3'>
                        <span className='list-title'>Selected ({filteredSelectedItems.length})</span>
                    </div>
                    {headerActions && (
                        <div className='col-3 my-3 d-flex justify-content-end align-items-center'>
                            {headerActions}
                        </div>
                    )}
                </div>

                <div className='row projects-section'>
                    <div className='col-12 py-2'>
                        <div className='input-group'>
                            <input
                                type='text'
                                className='form-control'
                                id='search-selected'
                                placeholder='Search by name'
                                aria-label='Search by name'
                                aria-describedby='search-addon-selected'
                                value={selectedFilter}
                                onChange={(e) => setSelectedFilter(e.target.value)}
                            />
                            <span className='input-group-text bg-transparent' id='search-addon-selected'>
                                <i className='far fa-magnifying-glass'></i>
                            </span>
                        </div>
                    </div>
                </div>

                <div className='row projects-section'>
                    <div className='col-12 list-container'>{renderSelectedList()}</div>
                </div>

                <div className='row projects-section-bottom'>
                    <div className='col-12'>
                        <button type='button' className='btn pacer61-purple-btn' onClick={() => deselectAll()}>
                            Deselect All
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DualListSelector;
